/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import { shallowEqual, useSelector } from 'react-redux';
import { selectServiceTierChangeRequestEmail } from '../../../../store';

export interface CancelServiceTierChangeRequestDialogProps {
  open: boolean;
  onYesClicked: () => void;
  onNoClicked: () => void;
}
const CancelServiceTierChangeRequestErrorDialog = ({
  open,
  onYesClicked,
  onNoClicked,
}: CancelServiceTierChangeRequestDialogProps) => {
  const serviceTierChangeRequestEmail = useSelector(selectServiceTierChangeRequestEmail, shallowEqual);

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '28px',
          letterSpacing: 0.2,
          color: '#FFFFFF',
          height: '76px',
        }}
      >
        Error
      </DialogTitle>
      <DialogContent>
        <Stack direction="column">
          <DialogContentText
            sx={{
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#FFFFFF',
              flex: 'none',
              order: 0,
              flexGrow: 1,
            }}
          >
            An error was encountered cancelling your Service Tier Change Request. Please try again.
          </DialogContentText>
          <DialogContentText
            sx={{
              marginTop: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#FFFFFF',
              flex: 'none',
              order: 0,
              flexGrow: 1,
            }}
          >
            {`If the error persists, please `}
            <Link
              color="#009DE0"
              underline="hover"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                window.location.href = `mailto:${serviceTierChangeRequestEmail || ''}`;
              }}
            >
              contact us
            </Link>
            .
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ height: '88px', borderTop: '1px solid #32424E' }}>
        <Button
          variant="outlined"
          sx={{
            height: '40px',
            width: '98px',
            borderColor: '#ffffff',
            color: '#ffffff',
            fontFamily: 'Source Sans Pro',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            marginRight: '8px',
            '&:hover': {
              borderColor: '#ffffff',
            },
          }}
          onClick={onNoClicked}
        >
          Close
        </Button>
        <Button
          variant="contained"
          sx={{
            height: '40px',
            width: '98px',
            backgroundColor: '#18ECFC',
            color: '#202E39',
            fontFamily: 'Source Sans Pro',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            marginRight: '8px',
            '&:hover': {
              color: '#202E39',
              backgroundColor: '#18ECFC',
            },
          }}
          onClick={onYesClicked}
          autoFocus
        >
          Retry
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default CancelServiceTierChangeRequestErrorDialog;
